/**
 * RFC 5545 .ics meeting-invite generation (Phase 5a-2).
 *
 * Pure + import.meta-free so jest can load it directly. Produces
 * METHOD:REQUEST (invite/update) or METHOD:CANCEL payloads suitable as a
 * text/calendar email part — Gmail/Outlook render accept/decline UI from it.
 */

export interface IcsPerson {
    name: string;
    email: string;
}

export interface InviteIcsArgs {
    /** Stable event id, e.g. `${eventId}@newgenus` — same UID across updates. */
    uid: string;
    /** Bump on every update; receivers use it to supersede older invites. */
    sequence: number;
    start: Date;
    end: Date;
    summary: string;
    description?: string;
    location?: string;
    organizer: IcsPerson;
    attendees: IcsPerson[];
    method?: 'REQUEST' | 'CANCEL';
    /** Injectable for deterministic tests; defaults to now. */
    dtStamp?: Date;
}

/** UTF-8 octet length — environment-free (no Buffer/TextEncoder needed). */
export function utf8Len(s: string): number {
    let len = 0;
    for (const ch of s) {
        const cp = ch.codePointAt(0) as number;
        len += cp <= 0x7f ? 1 : cp <= 0x7ff ? 2 : cp <= 0xffff ? 3 : 4;
    }
    return len;
}

/** 20260615T090000Z */
function icsUtc(date: Date): string {
    return date
        .toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}Z$/, 'Z');
}

/** Escape per RFC 5545 §3.3.11 (TEXT): backslash, semicolon, comma, newline. */
function escapeText(value: string): string {
    return value
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r?\n/g, '\\n');
}

/**
 * Fold a content line to <=75 octets per line (RFC 5545 §3.1); continuation
 * lines begin with a single space.
 */
function fold(line: string): string[] {
    const out: string[] = [];
    let remaining = line;
    let limit = 75;
    while (utf8Len(remaining) > limit) {
        // Cut on a character boundary that fits the octet limit.
        let cut = Math.min(remaining.length, limit);
        while (utf8Len(remaining.slice(0, cut)) > limit) cut--;
        out.push((out.length ? ' ' : '') + remaining.slice(0, cut));
        remaining = remaining.slice(cut);
        limit = 74; // continuation lines lose one octet to the leading space
    }
    out.push((out.length ? ' ' : '') + remaining);
    return out;
}

export function generateInviteIcs(args: InviteIcsArgs): string {
    const method = args.method ?? 'REQUEST';
    const dtStamp = args.dtStamp ?? new Date();

    const lines: string[] = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Newgenus//Workspace Calendar//EN',
        `METHOD:${method}`,
        'CALSCALE:GREGORIAN',
        'BEGIN:VEVENT',
        `UID:${args.uid}`,
        `SEQUENCE:${args.sequence}`,
        `DTSTAMP:${icsUtc(dtStamp)}`,
        `DTSTART:${icsUtc(args.start)}`,
        `DTEND:${icsUtc(args.end)}`,
        `SUMMARY:${escapeText(args.summary)}`,
    ];

    if (args.description) lines.push(`DESCRIPTION:${escapeText(args.description)}`);
    if (args.location) lines.push(`LOCATION:${escapeText(args.location)}`);

    lines.push(`ORGANIZER;CN=${escapeText(args.organizer.name)}:mailto:${args.organizer.email}`);
    for (const attendee of args.attendees) {
        lines.push(
            `ATTENDEE;CN=${escapeText(attendee.name)};RSVP=TRUE;PARTSTAT=NEEDS-ACTION:mailto:${attendee.email}`
        );
    }

    lines.push(`STATUS:${method === 'CANCEL' ? 'CANCELLED' : 'CONFIRMED'}`);
    lines.push('TRANSP:OPAQUE');
    lines.push('END:VEVENT');
    lines.push('END:VCALENDAR');

    return lines.flatMap(fold).join('\r\n') + '\r\n';
}
